import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import {
    Message,
    FlexBox,
    Orientation,
    Alignment,
    Kind,
    Text,
    type TextProps,
    Size,
    MessageProps,
} from '@lumapps/lumx/react';

import './index.scss';

const CLASSNAME = 'message-with-actions';

export interface MessageWithActionsProps extends Omit<MessageProps, 'children'> {
    /** The action(s) to display (usually one or more Buttons). */
    actions: React.ReactNode;
    /** The content of the message. */
    message: React.ReactNode;
    /** Optional props to customize the typography or layout of the text wrapper. */
    textProps?: Partial<TextProps>;
}

/**
 * A notification component that displays a message (with background) alongside action(s).
 * Useful for warnings or information requiring user intervention.
 *
 * @param MessageWithActionProps
 * @returns MessageWithAction
 */
export const MessageWithActions: React.FC<MessageWithActionsProps> = ({
    actions,
    className,
    hasBackground = true,
    kind = Kind.error,
    message,
    textProps,
    ...forwardedProps
}) => {
    const { block, element } = useClassnames(CLASSNAME);

    return (
        <Message {...forwardedProps} className={block([className])} hasBackground={hasBackground} kind={kind}>
            <FlexBox className={element('content')} hAlign={Alignment.center} orientation={Orientation.horizontal}>
                <Text as="p" {...textProps}>
                    {message}
                </Text>

                <FlexBox orientation={Orientation.horizontal} gap={Size.medium} className={element('actions')}>
                    {actions}
                </FlexBox>
            </FlexBox>
        </Message>
    );
};
